import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type { DeskLendingDateStatus, LendableDeskInfo } from 'garaje/services/desk-lending';
import { LENDING_STATUS } from 'garaje/utils/enums';

interface DeskOption {
  deskId: string;
  label: string;
  desk: LendableDeskInfo;
}

interface DeskLendPopupSignature {
  Args: {
    desks: LendableDeskInfo[];
    date: Date;
    lendingStatusByDeskAndDate: Record<string, Record<string, DeskLendingDateStatus>>;
    dateKey: string;
    onConfirm: (deskIds: string[]) => Promise<void>;
    onClose: () => void;
  };
}

export default class DeskLendPopup extends Component<DeskLendPopupSignature> {
  @tracked selectedDesks: DeskOption[] = [];
  @tracked isSubmitting = false;

  get deskOptions(): DeskOption[] {
    return this.args.desks
      .filter((desk) => {
        const status = this.args.lendingStatusByDeskAndDate[String(desk.deskId)]?.[this.args.dateKey]?.lendingStatus;
        return !status || status === LENDING_STATUS.NOT_LENT;
      })
      .map((desk) => ({
        deskId: String(desk.deskId),
        label: `${desk.deskName}, ${desk.floorName}`,
        desk,
      }));
  }

  get hasSelection(): boolean {
    return this.selectedDesks.length > 0;
  }

  get confirmDisabled(): boolean {
    return !this.hasSelection || this.isSubmitting;
  }

  @action
  onDesksChange(selected: DeskOption[]): void {
    this.selectedDesks = selected;
  }

  @action
  async handleConfirm(): Promise<void> {
    if (!this.hasSelection) return;
    this.isSubmitting = true;
    try {
      await this.args.onConfirm(this.selectedDesks.map((d) => d.deskId));
    } finally {
      this.isSubmitting = false;
    }
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @onClose={{@onClose}}
      @translucentOverlay={{true}}
      @targetAttachment="center"
    >
      <div class="p-6" style="min-width: 400px;" data-test-desk-lend-popup>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold">Select desks to lend</h2>
          <button
            type="button"
            class="btn btn--small btn--outline btn--cancel"
            aria-label="close"
            {{on "click" @onClose}}
          >
            &times;
          </button>
        </div>

        <div class="mb-4">
          <PowerSelectMultiple
            @options={{this.deskOptions}}
            @selected={{this.selectedDesks}}
            @onChange={{this.onDesksChange}}
            @searchEnabled={{false}}
            @placeholder="Select desks"
            @dropdownClass="in-modal"
            data-test-desk-lend-popup-select
            as |option|
          >
            {{option.label}}
          </PowerSelectMultiple>
        </div>

        <div class="flex justify-end">
          <button
            type="button"
            class="btn btn--small btn--brand"
            disabled={{this.confirmDisabled}}
            {{on "click" this.handleConfirm}}
            data-test-desk-lend-popup-confirm
          >
            {{if this.isSubmitting "Lending..." "Lend desk"}}
          </button>
        </div>
      </div>
    </ModalDialog>
  </template>
}
